import { useParams } from "react-router-dom";
import "./style.css";
import { useEffect, useRef, useState } from "react";
import { Heart, Trash } from "react-feather";
import { LoadingThreeCircle } from "../../../global-components/src/components/spinner/spiner";
import { LIMIT_REQUEST_LIKES } from "../../../utils/vars";
import { useAuth } from "../../../global-components/src";
import axios from "axios";
import { PACOCA_API_URL } from "../../../global-components/src/utils/vars";
import UserSearch from "../../../pages/Settings/SearchScreen/User/User";
import { UserSearchLoadin } from "../../../pages/Settings/SearchScreen/User/UserSearchLoading";
import { useReactionTypes } from "../../../contexts/ReactionsContext";

interface PostLikesContentProps {
    idPostSelected: number | null;
    open: boolean;
}

/* Renderiza o ícone da reação ao lado do avatar */
const ReactionBadge: React.FC<{ slug: string; emoji: string }> = ({ slug, emoji }) => {
    if (slug === "ball") {
        return <img src="/img/reacts/ball.png" className="like-reaction-badge like-reaction-badge--img" alt="bola" />;
    }
    return <span className="like-reaction-badge">{emoji}</span>;
};

const PostLikesContent: React.FC<PostLikesContentProps> = ({ idPostSelected, open }) => {
    const { id: postId } = useParams();
    const [likes, setLikes] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { token } = useAuth();
    const closeModalLikesRef = useRef<HTMLButtonElement | null>(null);
    const openModalLikesRef  = useRef<HTMLButtonElement | null>(null);
    const [end, setEnd] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const reactionTypes = useReactionTypes();

    /* filtro de reação selecionada (null = todas) */
    const [filterSlug, setFilterSlug] = useState<string | null>(null);

    useEffect(() => {
        if(window.innerWidth > 850) closeModalLikesRef?.current?.click();
    }, [location.pathname]);

    useEffect(() => {
        if (open) loadLikes(page);
    }, [open, page]);

    /* reset ao fechar modal */
    useEffect(() => {
        const handleModalClose = () => {
            setLikes([]);
            setIsLoading(false);
            setPage(1);
            setEnd(false);
            setHasMore(true);
            setFilterSlug(null);
        };
        const modalElement = document.getElementById("modal-likes");
        modalElement?.addEventListener("hidden.bs.modal", handleModalClose);
        return () => modalElement?.removeEventListener("hidden.bs.modal", handleModalClose);
    }, []);

    /* scroll infinito */
    useEffect(() => {
        const modalBody = document.getElementsByClassName("modal-body-likes")[0];
        const handleScroll = () => {
            if (!modalBody || !open) return;
            const { scrollTop, clientHeight, scrollHeight } = modalBody;
            if (scrollTop + clientHeight >= scrollHeight - 50 && !end && !isLoading && hasMore) {
                setPage(prev => prev + 1);
            }
        };
        modalBody?.addEventListener("scroll", handleScroll);
        window.addEventListener("scroll", handleScroll);
        return () => {
            modalBody?.removeEventListener("scroll", handleScroll);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [end, isLoading, open]);

    const loadLikes = async (newPage?: number) => {
        const currentPage = newPage ?? page + 1;
        setIsLoading(true);
        const url = `${PACOCA_API_URL}/posts/likes/${idPostSelected}/?limit=${LIMIT_REQUEST_LIKES}&page=${currentPage}`;
        try {
            const response = await axios.get(url, {
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            const incoming = response.data.likes as any[];
            if (incoming.length > 0) {
                setLikes(prev => {
                    const existingIds = new Set(prev.map(f => f.id));
                    const newItems = incoming.filter(f => !existingIds.has(f.id));
                    setHasMore(!(incoming.length < LIMIT_REQUEST_LIKES));
                    return [...prev, ...newItems];
                });
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Erro ao carregar curtidas:", error);
            setEnd(true);
        } finally {
            setIsLoading(false);
        }
    };

    /* reações únicas presentes na lista para montar os filtros */
    const presentSlugs = Array.from(new Set(likes.map(l => l.reaction_slug as string)));
    const filteredLikes = filterSlug ? likes.filter(l => l.reaction_slug === filterSlug) : likes;

    return (
        <>
            {/* ── Filtros por reação ── */}
            {presentSlugs.length > 1 && (
                <div className="likes-filter-bar">
                    <button
                        className={`likes-filter-btn${!filterSlug ? " active" : ""}`}
                        onClick={() => setFilterSlug(null)}
                    >
                        Todos · {likes.length}
                    </button>

                    {reactionTypes
                        .filter(rt => presentSlugs.includes(rt.slug))
                        .map(rt => {
                            const count = likes.filter(l => l.reaction_slug === rt.slug).length;
                            return (
                                <button
                                    key={rt.slug}
                                    className={`likes-filter-btn${filterSlug === rt.slug ? " active" : ""}`}
                                    onClick={() => setFilterSlug(prev => prev === rt.slug ? null : rt.slug)}
                                >
                                    {rt.slug === "ball"
                                        ? <img src="/img/reacts/ball.png" className="filter-ball-img" alt="bola" />
                                        : rt.emoji
                                    }
                                    &nbsp;{count}
                                </button>
                            );
                        })}
                </div>
            )}

            {/* ── Lista vazia ── */}
            {!isLoading && likes.length === 0 && (
                <div className="row user-select-none row-empty" style={{ paddingTop: 160 }}>
                    <span className="span-empty">
                        <Heart className="icon-empty" data-feather="heart" />
                        <h3 className="w-100 text-center">Seja o primeiro a curtir</h3>
                    </span>
                </div>
            )}

            {/* ── Usuários ── */}
            {filteredLikes.map((user) => (
                <div key={user.id} style={{ position: "relative", display: "inline-block", width: "100%" }}>
                    <UserSearch
                        clasImgAccount="img-likes"
                        clasNameSearch="name-likes"
                        user={user}
                    />
                    {/* badge sobreposto ao avatar */}
                    <div className="like-reaction-badge-wrapper">
                        <ReactionBadge slug={user.reaction_slug ?? "like"} emoji={user.reaction_emoji ?? "❤️"} />
                    </div>
                </div>
            ))}

            {/* ── Loading ── */}
            {isLoading && (
                <div className="row user-select-none">
                    <div style={{ padding: 10 }}>
                        <UserSearchLoadin quantity={likes.length ? 1 : 6} loading={isLoading} />
                    </div>
                </div>
            )}

            <button className="d-none" ref={openModalLikesRef} data-bs-dismiss="modal" data-bs-toggle="modal" data-bs-target="#modal-likes">a</button>
            <button className="d-none" ref={closeModalLikesRef} data-bs-dismiss="modal">a</button>
        </>
    );
};

export default PostLikesContent;